"use client"

import type React from "react"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { signIn } from "next-auth/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Shield, ArrowLeft, Loader2 } from "lucide-react"

export function LoginForm() {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [twoFactorCode, setTwoFactorCode] = useState("")
  const [rememberMe, setRememberMe] = useState(true) // По умолчанию включено
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [requires2FA, setRequires2FA] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    try {
      // Получаем данные для сессии
      const userAgent = navigator.userAgent;

      // Если уже на шаге 2FA - сразу авторизуем с кодом
      if (requires2FA) {
        const result = await signIn("credentials", {
          email,
          password,
          twoFactorCode,
          userAgent,
          rememberMe: rememberMe ? "true" : "false",
          redirect: false,
        })

        if (result?.error) {
          setError("Неверный код подтверждения")
          setTwoFactorCode("")
        } else if (result?.ok) {
          router.push("/dashboard")
          router.refresh()
        }
        setIsLoading(false)
        return
      }

      // Шаг 1: Проверяем нужен ли 2FA
      const check2FARes = await fetch("/api/auth/check-2fa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      })

      // Rate-limit от nginx/edge может прийти не-JSON ответом (HTML 429),
      // поэтому сначала проверяем статус и Content-Type.
      if (check2FARes.status === 429) {
        setError("Слишком много попыток. Подождите пару минут и попробуйте снова.")
        setIsLoading(false)
        return
      }

      const contentType = check2FARes.headers.get("content-type") || ""
      if (!contentType.includes("application/json")) {
        setError("Сервис временно недоступен. Попробуйте позже.")
        setIsLoading(false)
        return
      }

      const check2FAData = await check2FARes.json()

      if (check2FAData.error) {
        setError(check2FAData.error)
        setIsLoading(false)
        return
      }

      if (check2FAData.requires2FA) {
        // Показываем форму 2FA
        setRequires2FA(true)
        setIsLoading(false)
        return
      }

      // Если 2FA не нужен - обычная авторизация
      const result = await signIn("credentials", {
        email,
        password,
        userAgent,
        rememberMe: rememberMe ? "true" : "false",
        redirect: false,
      })

      if (result?.error) {
        setError("Неверный email или пароль")
      } else if (result?.ok) {
        router.push("/dashboard")
        router.refresh()
      }
    } catch {
      setError("Произошла ошибка при входе")
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setRequires2FA(false)
    setTwoFactorCode("")
    setError("")
  }

  // Форма ввода 2FA кода
  if (requires2FA) {
    return (
      <div className="space-y-6">
        <div className="space-y-2 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Двухфакторная аутентификация</h2>
          <p className="text-muted-foreground text-sm">
            Введите 6-значный код из приложения аутентификации
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="twoFactorCode">Код подтверждения</Label>
            <Input 
              id="twoFactorCode" 
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              required 
              className="h-14 text-center text-2xl tracking-[0.5em] font-mono"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={isLoading}
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-center">
              Или введите один из резервных кодов
            </p>
          </div>

          <Button type="submit" className="w-full h-11 text-base" disabled={isLoading || twoFactorCode.length < 6}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Проверка...
              </>
            ) : "Подтвердить"}
          </Button>

          <Button 
            type="button" 
            variant="ghost" 
            className="w-full" 
            onClick={handleBack}
            disabled={isLoading}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Назад
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground pt-4">© advcrm.ru 2025</p>
      </div>
    )
  }

  // Основная форма логина
  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h2 className="text-3xl font-bold tracking-tight text-balance">Добро пожаловать</h2>
        <p className="text-muted-foreground text-balance">Войдите в свою учетную запись MPGO Реклама</p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input 
            id="email" 
            type="email" 
            placeholder="name@mpgo.ru" 
            required 
            className="h-11"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Пароль</Label>
            <a href="#" className="text-sm text-primary hover:underline">
              Забыли пароль?
            </a>
          </div>
          <Input 
            id="password" 
            type="password" 
            required 
            className="h-11"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox 
            id="remember" 
            checked={rememberMe}
            onCheckedChange={(checked) => setRememberMe(checked === true)}
          />
          <label
            htmlFor="remember"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none"
          >
            Запомнить меня
          </label>
        </div>

        <Button type="submit" className="w-full h-11 text-base" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Вход...
            </>
          ) : "Войти"}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground pt-4">© advcrm.ru 2025</p>
    </div>
  )
}
